import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Trans} from '@common/i18n/trans';
import {FormattedPrice} from '@common/i18n/formatted-price';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {CheckIcon} from '@common/icons/material/Check';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}

export function PricingTable({
  selectedCycle,
  className,
  productLoader,
}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div
      className={clsx(
        'flex flex-col items-stretch gap-20 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:items-start md:justify-center',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}

function PlanList({plans, selectedPeriod}: PlanListProps) {
  const {isLoggedIn, isSubscribed, user} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);
  const currentSubscription = user?.subscriptions?.find(sub => sub.valid);
  const currentProductId = currentSubscription?.product_id;
  const currentPlanAmount = currentSubscription?.price?.amount ?? 0;

  return (
    <Fragment>
      {filteredPlans.map((plan, index) => {
        const isFirst = index === 0;
        const isLast = index === filteredPlans.length - 1;
        const price = findBestPrice(selectedPeriod, plan.prices);
        const planAmount = price?.amount ?? 0;
        const isCurrentPlan = currentProductId === plan.id;
        const isRecommended = !!plan.recommended && !isCurrentPlan;
        const canUpgradeToPlan =
          !isSubscribed || (!isCurrentPlan && planAmount > currentPlanAmount);

        let upgradeRoute: string | undefined;
        if (!isLoggedIn) {
          upgradeRoute = `/register?redirectFrom=pricing`;
        }
        if (isSubscribed && canUpgradeToPlan) {
          upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
        }
        if (isLoggedIn && !isSubscribed && !plan.free) {
          upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
        }

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx(
              'relative flex w-full flex-col rounded-2xl border transition-shadow md:min-w-260 md:max-w-340',
              isFirst && 'md:ml-auto',
              isLast && 'md:mr-auto',
              isRecommended
                ? 'border-primary shadow-[0_0_0_2px] shadow-primary/30 md:-mt-12 md:pb-12 md:pt-0'
                : 'border-divider shadow-sm hover:shadow-md',
              isCurrentPlan && 'border-primary/40',
            )}
          >
            {/* Recommended ribbon */}
            {isRecommended && (
              <div className="rounded-t-2xl bg-primary px-20 py-10 text-center text-xs font-bold uppercase tracking-widest text-white">
                <Trans message="Most popular" />
              </div>
            )}

            <div className={clsx('flex flex-1 flex-col p-28', isRecommended && 'pt-24')}>

              {/* Header */}
              <div className="mb-24">
                <div className="mb-8 flex items-center gap-10">
                  <span className="text-xl font-bold">
                    <Trans message={plan.name} />
                  </span>
                  {isCurrentPlan && (
                    <span className="inline-flex items-center gap-4 rounded-full bg-primary/10 px-10 py-3 text-[11px] font-semibold text-primary">
                      <CheckIcon size="xs" />
                      <Trans message="Current" />
                    </span>
                  )}
                </div>
                {plan.description && (
                  <p className="text-sm leading-relaxed text-muted">
                    <Trans message={plan.description} />
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="mb-28">
                {price ? (
                  <FormattedPrice
                    priceClassName="text-4xl font-extrabold tracking-tight"
                    periodClassName="text-muted text-sm ml-4"
                    variant="separateLine"
                    price={price}
                  />
                ) : (
                  <div className="text-4xl font-extrabold tracking-tight">
                    <Trans message="Free" />
                  </div>
                )}
              </div>

              {/* CTA */}
              {isCurrentPlan ? (
                <Button
                  variant="outline"
                  color="primary"
                  className="w-full"
                  size="md"
                  disabled
                >
                  <Trans message="Current plan" />
                </Button>
              ) : upgradeRoute ? (
                <Button
                  variant={isRecommended ? 'flat' : 'outline'}
                  color="primary"
                  className="w-full"
                  size="md"
                  elementType={Link}
                  onClick={() => {
                    if (isLoggedIn || !price || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price.id,
                    });
                  }}
                  to={upgradeRoute}
                >
                  {plan.free ? (
                    <Trans message="Get started" />
                  ) : isSubscribed ? (
                    <Trans message="Switch plan" />
                  ) : (
                    <Trans message="Get started" />
                  )}
                </Button>
              ) : null}

              {/* Feature list */}
              <ProductFeatureList product={plan} />
            </div>
          </m.div>
        );
      })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton />
      <PlanSkeleton />
      <PlanSkeleton />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-2xl border px-28 py-32 shadow-sm md:max-w-340"
    >
      <Skeleton className="mb-10 h-20 w-1/2" />
      <Skeleton className="mb-24 h-14 w-3/4" />
      <Skeleton className="mb-8 h-40 w-2/3" />
      <Skeleton className="mb-28 h-14 w-1/3" />
      <Skeleton className="mb-32 h-40 rounded-full" />
      <Skeleton className="mb-12 h-14" />
      <Skeleton className="mb-12 h-14 w-4/5" />
      <Skeleton className="mb-12 h-14 w-3/4" />
      <Skeleton className="h-14 w-4/5" />
    </m.div>
  );
}
